import type { Asset, FilesCapabilities } from '@odo/shared-ui';
import { FilesPanel } from '@odo/shared-ui';
import React, { useMemo } from 'react';
import api, { AUTH_TOKEN_STORAGE_KEY } from '../../api/config';
import { createCbctScanStorage } from '../../services/cbctScanStorage';
import { reportError } from '../../services/errorReporting';
import { filesApi } from '../../services/filesApi';
import { captureUploadError } from '../../observability/sentry';

interface PatientFilesTabProps {
    patientId: string;
    patientName?: string;
    assets: Record<string, Asset>;
    isLoading?: boolean;
    error?: unknown;
    refreshAssets: () => Promise<void>;
    /** Omits the outer card chrome when the parent already supplies one. Defaults to false. */
    hideCardChrome?: boolean;
}

const PatientFilesTab: React.FC<PatientFilesTabProps> = ({
    patientId,
    patientName,
    assets,
    isLoading = false,
    error,
    refreshAssets,
    hideCardChrome = false
}) => {
    // Stable per-patient persistence handle for CBCT markings — the viewer
    // re-loads whenever this identity changes, so it must be memoized.
    const cbctStorage = useMemo(() => createCbctScanStorage(patientId), [patientId]);

    const capabilities = useMemo<FilesCapabilities>(
        () => ({
            canDelete: true,
            canRename: false,
            canReclassify: false,
            cbctReadOnly: true,
            showUploadedBy: false,
            enablePreviewNavigation: true,
            enableMultiStlPreview: false,
            tooltips: 'native',
            onUploadError: (error, ctx) => {
                const message = error instanceof Error ? error.message : String(error);
                reportError({
                    source: 'dashboard',
                    message: `File upload failed: ${message}`,
                    stack: ctx.errorStack,
                    url: window.location.href,
                    pathname: window.location.pathname,
                    userAgent: navigator.userAgent,
                    errorType: 'upload_failure',
                    metadata: {
                        patientId,
                        fileName: ctx.fileName,
                        fileSize: ctx.fileSize,
                        context: 'files_tab'
                    }
                });
                captureUploadError(error, {
                    fileName: ctx.fileName,
                    fileSize: ctx.fileSize,
                    uploadContext: 'files_tab'
                });
            }
        }),
        [patientId]
    );

    return (
        <FilesPanel
            patientId={patientId}
            patientName={patientName}
            assets={assets}
            isLoading={isLoading}
            error={error}
            refresh={refreshAssets}
            api={filesApi}
            capabilities={capabilities}
            cbctScanStorage={cbctStorage}
            viewerApiBaseUrl={api.defaults.baseURL ?? ''}
            getAuthToken={() => localStorage.getItem(AUTH_TOKEN_STORAGE_KEY)}
            chrome={hideCardChrome ? 'none' : 'card'}
            storageKeyPrefix="dashboard_files"
        />
    );
};

export { PatientFilesTab };
